import { Button, Stepper, Text } from '@mantine/core';
import { useThrottledValue } from '@mantine/hooks';
import { IconArrowLeft, IconCheck } from '@tabler/icons-react';
import { motion } from 'motion/react';
import { useState } from 'react';

import { CheckboxSlide } from './CheckboxSlide.tsx';
import { ConfirmationSlide } from './ConfirmationSlide.tsx';
import { OptionSlide } from './OptionSlide.tsx';
import classes from './StepperCard.module.css';
import { Bubble } from '#plugin-chat/components/bubble/Bubble.tsx';
import {
  SLIDE_TYPE_META,
  type SlideType,
  type StepperSlide,
} from '#plugin-chat/components/tools/ask-user-question/schema.ts';

/**
 * Single source of truth mapping each slide type to its rendering component.
 * Adding a slide type to {@link SlideType} forces a new entry here at compile
 * time (the `Record<SlideType, …>` shape makes a missing key an error). The
 * selection behavior for each type lives in {@link SLIDE_TYPE_META}.
 */
const SLIDE_REGISTRY: Record<SlideType, typeof OptionSlide | typeof CheckboxSlide | typeof ConfirmationSlide> = {
  option: OptionSlide,
  checkbox: CheckboxSlide,
  confirmation: ConfirmationSlide,
};

export interface StepperCardProps {
  /** Slides to display in the stepper */
  slides: StepperSlide[];
  /** Current slide index (0-based) */
  currentSlide: number;
  /** Callback when user makes a selection. Called with slide index and selected option IDs. */
  onSelect: (slideIndex: number, selectedIds: string[]) => void;
  /** Callback when user navigates back. Only shown if currentSlide > 0. */
  onBack?: () => void;
  /** Whether options are still streaming in. Shows skeleton placeholder when true. */
  isStreaming?: boolean;
}

/**
 * Multi-slide stepper card for user prompts.
 *
 * Renders differently based on slide count:
 * - Single slide (slides.length = 1): Inline card without stepper chrome
 * - Multi-slide (slides.length > 1): Card with Mantine Stepper for progress
 *
 * Supports three slide types:
 * - `option`: Radio-style selection (exactly one)
 * - `checkbox`: Chip-based multi-select (one or more)
 * - `confirmation`: Yes/No with cancel/confirm styling
 */
export function StepperCard({ slides, currentSlide, onSelect, onBack, isStreaming }: StepperCardProps) {
  const isMultiSlide = slides.length > 1;
  const slide = useThrottledValue(slides[currentSlide], 200);

  if (!slide) {
    return null;
  }

  return (
    <Bubble role="assistant">
      <div className={classes.stepperCard}>
        {isMultiSlide && <StepperCard.Progress currentSlide={currentSlide} slides={slides} />}

        <motion.div
          animate={{ opacity: 1, x: 0 }}
          className={classes.slideContainer}
          initial={{ opacity: 0, x: -50 }}
          key={currentSlide}
          transition={{ duration: 0.5 }}
        >
          <StepperCard.Slide
            isStreaming={isStreaming}
            onBack={currentSlide > 0 ? onBack : undefined}
            onSelect={(ids) => onSelect(currentSlide, ids)}
            slide={slide}
          />
        </motion.div>
      </div>
    </Bubble>
  );
}

interface ProgressProps {
  slides: StepperSlide[];
  currentSlide: number;
}

function getStepDescription(index: number, currentSlide: number): string | undefined {
  if (index < currentSlide) {
    return 'Complete';
  }
  if (index === currentSlide) {
    return 'Current';
  }
  return undefined;
}

function Progress({ slides, currentSlide }: ProgressProps) {
  return (
    <Stepper active={currentSlide} className={classes.stepper} size="sm">
      {slides.map((slide, index) => (
        <Stepper.Step
          completedIcon={<IconCheck size={14} />}
          description={getStepDescription(index, currentSlide)}
          key={slide.title}
          label={slide.title}
        />
      ))}
    </Stepper>
  );
}

interface SlideProps {
  slide: StepperSlide;
  onSelect: (selectedIds: string[]) => void;
  onBack?: () => void;
  isStreaming?: boolean;
}

function Slide({ slide, onSelect, onBack, isStreaming }: SlideProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  if (!slide.type) {
    return null;
  }

  const SlideContent = SLIDE_REGISTRY[slide.type];
  const { selectionMode } = SLIDE_TYPE_META[slide.type];

  const handleOptionClick = (ids: string[]) => {
    if (selectionMode === 'multi') {
      setSelectedIds(ids);
      return;
    }

    // single-select slide types - immediate action
    onSelect(ids);
  };

  const handleMultiSelectConfirm = () => {
    if (selectedIds.length > 0) {
      onSelect(selectedIds);
    }
  };

  return (
    <div className={classes.slide}>
      <div className={classes.question}>{slide.question}</div>

      <SlideContent
        isStreaming={isStreaming}
        onOptionClick={handleOptionClick}
        options={slide.options}
        selectedIds={selectedIds}
      />

      <div className={classes.slideActions}>
        {onBack && (
          <Button leftSection={<IconArrowLeft size={16} />} onClick={onBack} variant="subtle">
            Back
          </Button>
        )}

        {selectionMode === 'multi' && (
          <Button disabled={selectedIds.length === 0} ml="auto" onClick={handleMultiSelectConfirm} size="md">
            Continue
            <Text ml="xs" opacity={0.6}>
              ({selectedIds.length} selected)
            </Text>
          </Button>
        )}
      </div>
    </div>
  );
}

// Attach sub-components for compound pattern
StepperCard.Progress = Progress;
StepperCard.Slide = Slide;
